import React, { useEffect, useState, useRef, useCallback } from "react";
import { MapContainer, TileLayer, GeoJSON, LayersControl } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapHomeControl, MapInfoControl, MapLegendControl } from "../../../common/MapControls/MapControls";
//import "../../Earthquake/HealthCenters/HealthCenters.css";

const INITIAL_VIEW_STATE = {
  longitude: 125.7275,
  latitude: -8.8742,
  zoom: 8,
};

const ExposureRA = () => {
  const [boundaryData, setBoundaryData] = useState(null);
  const [hospitalData, setHospitalData] = useState(null);
  const [chcData, setChcData] = useState(null);
  const [selectedData, setSelectedData] = useState(null);
  const [legendVisible, setLegendVisible] = useState(false);
  const legendRef = useRef(null);

  // Leaflet's own drag/click handling swallows clicks on overlay controls
  // unless propagation to the map is explicitly disabled on the DOM node.
  const detailsBoxRef = useCallback((node) => {
    if (node) {
      L.DomEvent.disableClickPropagation(node);
      L.DomEvent.disableScrollPropagation(node);
    }
  }, []);

  useEffect(() => {
    // Load Boundary Data
    fetch("/DATA/OSMbased_adm1_boundary.json")
      .then((response) => response.json())
      .then((data) => setBoundaryData(data))
      .catch((error) => console.error("Error loading boundary data:", error));

    // Load Hospital Data
    fetch("/DATA/Cyclone/Vulnerability/Hospital.json")
      .then((response) => response.json())
      .then((data) => setHospitalData(data))
      .catch((error) => console.error("Error loading hospital data:", error));

    // Load CHC Data
    fetch("/DATA/Cyclone/Vulnerability/CommunityHealthCenter.json")
      .then((response) => response.json())
      .then((data) => setChcData(data))
      .catch((error) => console.error("Error loading CHC data:", error));
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (legendRef.current && !legendRef.current.contains(event.target)) {
        setLegendVisible(false);
      }
    };

    if (legendVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [legendVisible]);

  const hospitalIcon = new L.Icon({
    iconUrl: "/DATA/Cyclone/Vulnerability/hospital-marker.png",
    iconSize: [25, 25],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });

  const chcIcon = new L.Icon({
    iconUrl: "/DATA/Cyclone/Vulnerability/CommunityHealthCenter.png",
    iconSize: [25, 25],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });

  const extractHospitalData = (properties) => ({
    ID: properties.ID,
    FACILITY: properties.FACILITY,
    DISTRICT: properties.DISTRICT,
    CODE: properties.CODE,
    OWNER_: properties.OWNER_,
    MANAGEMENT: properties.MANAGEMENT,
    VISITMNTH: properties.VISITMNTH,
    PHYSICIAN: properties.PHYSICIAN,
    NURSE: properties.NURSE,
    MIDWIFE: properties.MIDWIFE,
    DENTAL: properties.DENTAL,
    LAB: properties.LAB,
    ASS_NURSE: properties.ASS_NURSE,
    OTHER: properties.OTHER,
    ASSUMPTION: properties.ASSUMPTION,
  });

  const extractChcData = (properties) => ({
    OBJECTID: properties.OBJECTID,
    ID: properties.ID,
    DISTR_NAME: properties.DISTR_NAME,
    SUBDS_NAME: properties.SUBDS_NAME,
    CHC: properties.CHC,
    CHECK_DATE: properties.CHECK_DATE,
    WAYPOINT: properties.WAYPOINT,
    WP_SHAPEFI: properties.WP_SHAPEFI,
    BY_: properties.BY_,
  });

  return (
    <>
    <h6>Click the markers to view the data.</h6>
      <MapContainer
        center={[INITIAL_VIEW_STATE.latitude, INITIAL_VIEW_STATE.longitude]}
        zoom={INITIAL_VIEW_STATE.zoom}
        className="map-container"
      >
        <MapHomeControl />
        <MapInfoControl text="Road network and exposure to road accident risk across Timor-Leste." />
        <LayersControl position="topright">
          <LayersControl.BaseLayer name="White Theme" checked>
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="OpenStreetMap">
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
            />
          </LayersControl.BaseLayer>
        </LayersControl>

        {boundaryData && (
          <GeoJSON
            data={boundaryData}
            style={{
              color: "#000000", // Black outline
              weight: 2,
              opacity: 1,
              fillOpacity: 0, // Transparent fill
            }}
          />
        )}

        {hospitalData && (
          <GeoJSON
            data={hospitalData}
            pointToLayer={(feature, latlng) =>
              L.marker(latlng, { icon: hospitalIcon }).on("click", () =>
                setSelectedData(extractHospitalData(feature.properties))
              )
            }
          />
        )}

        {chcData && (
          <GeoJSON
            data={chcData}
            pointToLayer={(feature, latlng) =>
              L.marker(latlng, { icon: chcIcon }).on("click", () =>
                setSelectedData(extractChcData(feature.properties))
              )
            }
          />
        )}

        <MapLegendControl
          sections={[{
            items: [
              { label: "Hospitals", icon: <img src="/DATA/Cyclone/Vulnerability/hospital-marker.png" alt="Hospital Icon" style={{ width: "18px", height: "18px" }} /> },
              { label: "Community Health Centers", icon: <img src="/DATA/Cyclone/Vulnerability/CommunityHealthCenter.png" alt="CHC Icon" style={{ width: "18px", height: "18px" }} /> },
            ],
          }]}
        />

        {selectedData && (
          <div ref={detailsBoxRef} className="details-box">
            <h4>Details</h4>
            {Object.entries(selectedData).map(([key, value]) => (
              <p key={key}>
                <strong>{key}:</strong> {value}
              </p>
            ))}
            <button onClick={() => setSelectedData(null)}>Close</button>
          </div>
        )}
      </MapContainer>
    </>
  );
};

export default ExposureRA;